/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/jsx-no-constructed-context-values */
import App from 'next/app';
import Head from 'next/head';
import { useState, useEffect } from 'react';
import isMobile from 'ismobilejs';
import { ViewportProvider } from 'src/common/utils/responsive';
import { initGlobals } from 'src/common/utils/globals';
import { TravelAppContext } from 'src/common/utils/context';
import {
  CouponsContextProvider,
  PersuassionContextProvider,
  TripInfoContextProvider,
  PreviousPageContextProvider,
  PostPaymentDetailsProvider,
} from 'src/international-insurance/context';
import {
  ThemeProvider,
  AckoMainTheme,
} from '@acko-tech/building-blocks.ui.theme';
import { GlobalStyle } from 'src/common/styles';
import '../styles/globals.css';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { useRouter } from 'next/router';
import TagManager from 'react-gtm-module';
import {
  GROWTHBOOK_PROD,
  GROWTHBOOK_UAT,
  ACKO_GTM_ID,
} from 'src/common/utils/config';
import { UserProvider } from 'src/common/context-providers/user-provider';
import { initialiseAckoAnalytics } from 'src/common/utils/globals/initialiseAckoAnalytics';
import { HT } from 'src/common/utils/config/script';
import { PRODUCTION_ENVIRONMENT } from 'src/common/utils/api/constants';
import Cookies from 'js-cookie';
import globalStyles from 'src/common/styles/sass/common/common.module.scss';

const growthbook = new GrowthBook({
  apiHost: PRODUCTION_ENVIRONMENT ? GROWTHBOOK_PROD.HOST : GROWTHBOOK_UAT.HOST,
  clientKey: PRODUCTION_ENVIRONMENT ? GROWTHBOOK_PROD.KEY : GROWTHBOOK_UAT.KEY,
  enableDevMode: !PRODUCTION_ENVIRONMENT,
  trackingCallback: (experiment, result) => {
    console.log('Viewed Experiment', {
      experimentId: experiment.key,
      variationId: result.key,
    });
  },
});

function TravelApp(props: any) {
  const { Component, pageProps, isMobileDevice, query, host, cookie } = props;
  const router = useRouter();
  const isHT = router?.query?.utm_source === HT?.NAME;
  const [growthbookError, setGrowthbookError] = useState<boolean>(false);

  useEffect(() => {
    initGlobals();
    if (PRODUCTION_ENVIRONMENT)
      TagManager.initialize({ gtmId: ACKO_GTM_ID?.PROD });

    if (typeof window !== 'undefined') {
      localStorage?.removeItem('userDetails');
    }
  }, []);

  useEffect(() => {
    try {
      growthbook.loadFeatures();
      growthbook.setAttributes({ id: Cookies?.get('user_id') || '' });
    } catch (e: any) {
      setGrowthbookError(true);
    }
    initialiseAckoAnalytics();
  }, [router?.pathname]);

  return (
    <>
      <Head>
        <title>Acko | Travel</title>
        <meta name="description" content="Acko | Travel" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        {isHT && (
          <script id="ht-script" type="text/javascript">
            {HT.SCRIPT}
          </script>
        )}
      </Head>
      <UserProvider>
        <TravelAppContext.Provider
          value={{
            cookie,
            growthbookError,
          }}
        >
          <GrowthBookProvider growthbook={growthbook}>
            <ViewportProvider isMobileDevice={isMobileDevice}>
              <ThemeProvider theme={AckoMainTheme}>
                <>
                  <GlobalStyle />
                  <TripInfoContextProvider>
                    <PersuassionContextProvider>
                      <PreviousPageContextProvider>
                        <CouponsContextProvider>
                          <PostPaymentDetailsProvider>
                            <main className={globalStyles?.fontStyles}>
                              <Component
                                {...pageProps}
                                query={query}
                                host={host}
                                cookie={cookie}
                              />
                            </main>
                          </PostPaymentDetailsProvider>
                        </CouponsContextProvider>
                      </PreviousPageContextProvider>
                    </PersuassionContextProvider>
                  </TripInfoContextProvider>
                </>
              </ThemeProvider>
            </ViewportProvider>
          </GrowthBookProvider>
        </TravelAppContext.Provider>
      </UserProvider>
    </>
  );
}

TravelApp.getInitialProps = async (context: any) => {
  const appProps = await App.getInitialProps(context);
  const userAgent =
    typeof window === 'undefined'
      ? context?.ctx?.req?.headers['user-agent']
      : window?.navigator?.userAgent;

  const isMobileDevice = isMobile(userAgent).any;

  return {
    ...appProps,
    isMobileDevice,
    query: context?.ctx?.query,
    host: context?.ctx?.req?.headers?.host,
    cookie: context?.ctx?.req?.headers?.cookie || '',
  };
};

export default TravelApp;
